import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiFieldText, EuiFormRow, EuiLoadingContent, EuiRadioGroup, EuiSpacer, EuiSwitch, EuiText } from '@elastic/eui'

import { BudgetFrequency, Campaign, CampaignActiveStatus, CampaignStatus, CampaignType, FrequencyUnit } from '../../../../api/interfaces'
import { IntervalUnit } from '../../../../api/interfaces/Bundle'
import { AdPlacementType, CampaignDelivery } from '../../../../api/interfaces/CampaignDelivery'
import { campaignsApi, useUpdateCampaignDeliveryMutation, useUpdateCampaignMutation, useUpdateSavedForLaterCampaignMutation } from '../../../../api/rtkQueryApi/platform/campaignsApi'
import { showSuccessToast } from '../../../../app/appSlice'
import { RootState } from '../../../../app/rootReducer'
import BudgetChangeModal from '../../../../components/Modals/BudgetChangeModal'
import { DefaultDayParts } from '../../../../components/targeters/DayPartPicker'
import { useWhiteLabel } from '../../../../whiteLabel/WhiteLabelContext'
import { AgencyV2CampaignBudgetSetter, BbMinWeeklyBudget, BudgetMethod, CampaignBudget, getMinBudgetForMethod, InternetMinWeeklyBudget, TvMinWeeklyBudget } from '../../AgencyV2CampaignBudgetSetter'
import UnpaidCallOut from '../UnpaidCallOut'
import { fetchCampaign, setCampaign } from '../campaignSlice'
import { useCampaignEdit } from './CampaignEditContext'
import { EditCampaignDeliveryStrategy } from './forms/EditCampaignDeliveryStrategy'

export const EditCampaignDetails: React.FC = () => {
  let { campaignId } = useParams<{ campaignId: string }>()
  const { currentAccount, isStaffUser } = useSelector((state: RootState) => state.app)
  const { campaign } = useSelector((state: RootState) => state.campaign)
  const [updateCampaign] = useUpdateCampaignMutation()
  const [updateSavedForLaterCampaign] = useUpdateSavedForLaterCampaignMutation()
  const dispatch = useDispatch()
  const [amount, setAmount] = useState<number>(0)
  const [isValid, setIsValid] = useState<boolean>(true)

  const [name, setName] = useState<string>('')
  const [maxCpm, setMaxCpm] = useState<number | undefined>()
  const [budgetInputValue, setBudgetInputValue] = useState<string>('')

  const [isBudgetChangeModalVisible, setIsBudgetChangeModalVisible] = useState(false)
  const [campaignBudget, setCampaignBudget] = useState<CampaignBudget>({
    budget: 0,
    method: BudgetMethod.Indefinite
  })

  const [scheduleSettings, setScheduleSettings] = useState<{
    indefinite: { budget: number; intervalUnit: IntervalUnit }
    intervals: { budget: number; start?: moment.Moment; totalIntervals: number; intervalUnit: IntervalUnit }
    dates: { budget: number; start?: moment.Moment; end?: moment.Moment }
  }>({
    indefinite: { budget: 0, intervalUnit: IntervalUnit.Week },
    intervals: { budget: 0, totalIntervals: 1, intervalUnit: IntervalUnit.Week },
    dates: { budget: 0 }
  })

  const whiteLabel = useWhiteLabel()
  const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false)
  const [campaignDelivery, setCampaignDelivery] = useState<CampaignDelivery>()

  const [updateCampaignDelivery] = useUpdateCampaignDeliveryMutation()
  const [isSaving, setIsSaving] = useState(false)
  const { registerSave, markDirty } = useCampaignEdit()

  const canBudgetBeEmpty = useMemo(() => {
    if (!campaign) {
      return false
    }

    return !campaign.hasEditedBudget && (campaign.status === CampaignStatus.SavedForLater || campaign.status === CampaignStatus.Disapproved)
  }, [campaign])

  const getMinWeeklyBudget = useCallback(() => {
    if (!campaign) {
      return TvMinWeeklyBudget
    }
    if (campaign.type === CampaignType.Internet) {
      return InternetMinWeeklyBudget
    } else if (campaign.type === CampaignType.TV) {
      return TvMinWeeklyBudget
    } else if (campaign.type === CampaignType.Billboard) {
      return BbMinWeeklyBudget
    } else if (campaign.type === CampaignType.Audio) {
      return TvMinWeeklyBudget
    }
    return TvMinWeeklyBudget
  }, [campaign])

  const getMinStartDate = useCallback(() => {
    if (!campaign?.activated || !campaign.startDate) {
      return isStaffUser ? moment() : moment().add(3, 'day')
    }

    const startDate = moment(campaign.startDate)
    if (startDate < moment()) {
      return startDate
    }

    return moment().add(3, 'day') < startDate ? moment().add(3, 'day') : startDate
  }, [campaign, isStaffUser])

  const deriveIntervalUnit = useCallback((campaign: any): IntervalUnit => {
    switch (campaign.budgetFrequency) {
      case BudgetFrequency.Weekly:
        return IntervalUnit.Week
      case BudgetFrequency.Monthly:
        return IntervalUnit.Month
      default:
        return IntervalUnit.Week
    }
  }, [])

  const deriveBudgetMethod = useCallback((campaign: Campaign): BudgetMethod => {
    if (campaign.budgetFrequency === BudgetFrequency.Total) {
      return BudgetMethod.Dates
    } else if (campaign?.flightSchedule && !campaign.flightSchedule.isIndefinite) {
      return BudgetMethod.Intervals
    } else {
      return BudgetMethod.Indefinite
    }
  }, [])

  const initializeScheduleSettings = useCallback(
    (campaign: any, intervalUnit: IntervalUnit) => {
      const baseBudget = canBudgetBeEmpty ? 0 : campaign.budget
      const minStartDate = getMinStartDate()

      return {
        indefinite: {
          budget: baseBudget,
          intervalUnit
        },
        intervals: {
          budget: baseBudget,
          start: campaign.startDate ? moment.utc(campaign.startDate) : minStartDate,
          totalIntervals: campaign.flightSchedule?.totalIntervals ?? 3,
          intervalUnit
        },
        dates: {
          budget: baseBudget,
          start: campaign.startDate ? moment.utc(campaign.startDate) : minStartDate,
          end: campaign.endDate ? moment.utc(campaign.endDate) : minStartDate.clone().add(3, 'months')
        }
      }
    },
    [canBudgetBeEmpty, getMinStartDate]
  )

  const initializeCampaignBudget = useCallback(
    (campaign: any, budgetMethod: BudgetMethod, intervalUnit: IntervalUnit): CampaignBudget => {
      const baseBudget = canBudgetBeEmpty ? 0 : campaign.budget

      return {
        budget: baseBudget,
        start: campaign.startDate ? moment.utc(campaign.startDate) : undefined,
        end: campaign.endDate ? moment.utc(campaign.endDate) : undefined,
        method: budgetMethod,
        totalIntervals: budgetMethod === BudgetMethod.Intervals ? campaign.flightSchedule?.totalIntervals : undefined,
        intervalUnit
      }
    },
    [canBudgetBeEmpty]
  )

  useEffect(() => {
    if (campaign) {
      if (campaign.type === CampaignType.Internet) {
        const inventorySettings = campaign?.advancedTargeting?.inventorySettings ?? undefined
        const deviceSettings = campaign?.advancedTargeting?.deviceSettings ?? undefined

        setCampaignDelivery({
          dayParts: campaign.advancedTargeting?.dayParts ?? DefaultDayParts,
          frequency: campaign.advancedTargeting?.frequency ?? 0,
          frequencyUnit: campaign.advancedTargeting?.frequencyUnit ?? FrequencyUnit.week,
          inventorySettings: inventorySettings,
          deviceSettings: deviceSettings,
          placement: AdPlacementType.Everywhere,
          categories: [],
          specific: [],
          customerOptimizationStrategy: campaign.customerOptimizationStrategy
        })
      } else if (campaign.type === CampaignType.TV) {
        setCampaignDelivery({
          dayParts: campaign.tvTargeting?.dayParts ?? DefaultDayParts,
          frequency: campaign.tvTargeting?.frequency ?? 0,
          frequencyUnit: campaign.tvTargeting?.frequencyUnit ?? 0,
          inventorySettings: undefined,
          deviceSettings: undefined,
          placement: AdPlacementType.Everywhere,
          categories: [],
          specific: [],
          customerOptimizationStrategy: campaign.customerOptimizationStrategy
        })
      }
    }
  }, [campaign])

  useEffect(() => {
    if (campaign) {
      const budgetFrequencyAsIntervalUnit = deriveIntervalUnit(campaign)
      const intervalUnit = campaign.flightSchedule?.intervalUnit ?? budgetFrequencyAsIntervalUnit
      const budgetMethod = deriveBudgetMethod(campaign)

      setScheduleSettings(initializeScheduleSettings(campaign, intervalUnit))
      setCampaignBudget(initializeCampaignBudget(campaign, budgetMethod, intervalUnit))

      setName(campaign.name)
      setMaxCpm(campaign.maxCpm)
    }
  }, [campaign, whiteLabel, deriveIntervalUnit, deriveBudgetMethod, initializeScheduleSettings, initializeCampaignBudget])

  useEffect(() => {
    if (canBudgetBeEmpty && campaignBudget.budget <= 0) {
      setBudgetInputValue('')
    } else {
      setBudgetInputValue(campaignBudget.budget.toString())
    }
  }, [campaignBudget.budget, canBudgetBeEmpty])

  useEffect(() => {
    setIsValid(isValidBudget(campaignBudget))
  }, [campaignBudget])

  const onBudgetChangeModalComplete = (shouldFetchCampaign: boolean) => {
    if (shouldFetchCampaign) {
      dispatch(fetchCampaign(currentAccount!.id, campaignId))
      dispatch(campaignsApi.util.invalidateTags([{ type: 'Campaign', id: campaignId }]))
    }
    setIsBudgetChangeModalVisible(false)
  }

  const onBudgetChangeModalCancel = () => {
    setIsBudgetChangeModalVisible(false)
  }

  const showBudgetChangeModal = (amount: number) => {
    if (campaign) {
      setAmount(amount)
      setIsBudgetChangeModalVisible(true)
    }
  }

  const onSave = useCallback(async () => {
    if (!campaign) {
      return
    }
    const amount = campaignBudget.budget - campaign.budget
    if (amount > 0 && !campaign.canRemove && campaign.activeStatus === CampaignActiveStatus.Active) {
      showBudgetChangeModal(amount)
    } else {
      setIsSaving(true)

      let updatedCampaign
      const baseRequest = {
        accountId: currentAccount!.id,
        campaignId: campaign.id,
        name: name,
        budget: campaignBudget.budget,
        maxCpm,
        startDate: campaignBudget.start,
        endDate: campaignBudget.end,
        totalIntervals: campaignBudget.totalIntervals
      }

      if ((campaign.status === CampaignStatus.SavedForLater || campaign.status === CampaignStatus.Disapproved)) {
        const savedForLaterRequest: any = {
          ...baseRequest,
          isIndefinite: campaignBudget.method === BudgetMethod.Indefinite,
          intervalUnit: campaignBudget.intervalUnit ?? IntervalUnit.Week,
          intervalUnitFrequency: 1
        }

        if (campaignBudget.budget > 0) {
          savedForLaterRequest.budget = campaignBudget.budget
        } else {
          delete savedForLaterRequest.budget
        }

        updatedCampaign = await updateSavedForLaterCampaign(savedForLaterRequest).unwrap()
      } else {
        updatedCampaign = await updateCampaign(baseRequest).unwrap()
      }

      if (!!campaignDelivery?.customerOptimizationStrategy && (updatedCampaign.type === CampaignType.Internet || updatedCampaign.type === CampaignType.TV)) {
        updatedCampaign = await updateCampaignDelivery({
          accountId: currentAccount!.id,
          campaignId: updatedCampaign.id,
          delivery: {
            inventorySettings: campaignDelivery.inventorySettings,
            deviceSettings: campaignDelivery.deviceSettings,
            frequency: campaignDelivery.frequency,
            frequencyUnit: campaignDelivery.frequencyUnit,
            dayParts: campaignDelivery.dayParts ?? DefaultDayParts,
            categories: updatedCampaign.categories,
            placement: AdPlacementType.Everywhere,
            specific: []
          },
          customerOptimizationStrategy: campaignDelivery.customerOptimizationStrategy
        }).unwrap()
      }

      dispatch(setCampaign(updatedCampaign))

      setIsSaving(false)
      dispatch(showSuccessToast('Saved Campaign'))
      dispatch(fetchCampaign(currentAccount!.id, campaignId))
    }
  }, [campaign, campaignBudget, campaignDelivery, name, maxCpm, currentAccount, campaignId, updateCampaign, updateSavedForLaterCampaign, updateCampaignDelivery, dispatch])

  useEffect(() => {
    registerSave('details', onSave)
  }, [onSave, registerSave])

  const getMinBudget = useCallback(() => {
    if (!campaign) {
      return getMinBudgetForMethod(InternetMinWeeklyBudget, campaignBudget)
    }

    if (!campaign || campaign.type === CampaignType.Internet) {
      return getMinBudgetForMethod(InternetMinWeeklyBudget, campaignBudget)
    } else if (campaign.type === CampaignType.TV) {
      return getMinBudgetForMethod(TvMinWeeklyBudget, campaignBudget)
    } else if (campaign.type === CampaignType.Billboard) {
      return getMinBudgetForMethod(BbMinWeeklyBudget, campaignBudget)
    }

    return getMinBudgetForMethod(InternetMinWeeklyBudget, campaignBudget)
  }, [campaign, campaignBudget])

  const isBudgetValid = useCallback(() => {
    if (!campaign) {
      return false
    }

    if (Math.floor(campaignBudget.budget) !== campaignBudget.budget && campaignBudget.budget.toString().split('.')[1].length > 2) {
      return false
    }

    if (canBudgetBeEmpty && budgetInputValue === '') {
      return true
    }

    if (!canBudgetBeEmpty && campaignBudget.budget === campaign.budget) {
      return true
    }

    const minBudget = getMinBudget()
    const isBelowMinimum = (canBudgetBeEmpty && budgetInputValue !== '' && campaignBudget.budget != null && campaignBudget.budget < minBudget) || (!canBudgetBeEmpty && campaignBudget.budget != null && campaignBudget.budget < minBudget)
    return !isBelowMinimum
  }, [campaign, budgetInputValue, campaignBudget.budget, getMinBudget, canBudgetBeEmpty])

  const isValidBudget = useCallback(
    (budget: CampaignBudget): boolean => {
      if (!campaign) {
        return false
      }

      if (canBudgetBeEmpty) {
        return true
      }

      if (!canBudgetBeEmpty && budget.budget === campaign.budget) {
        return true
      }

      if (budget.method === BudgetMethod.Indefinite) {
        const minBudget = budget.intervalUnit === IntervalUnit.Month ? getMinWeeklyBudget() * 4 : getMinWeeklyBudget()
        return budget.budget >= minBudget && !budget.start && !budget.end
      } else if (budget.method === BudgetMethod.Dates) {
        return !!(budget.budget >= getMinBudget() && budget.start && budget.end && moment(budget.start).isBefore(budget.end) && (moment(budget.start).isSameOrAfter(moment(campaign.startDate), 'day') || moment(budget.start).endOf('day').isAfter(getMinStartDate().startOf('day'))))
      } else if (budget.method === BudgetMethod.Intervals) {
        const minBudget = budget.intervalUnit === IntervalUnit.Month ? getMinWeeklyBudget() * 4 : getMinWeeklyBudget()
        return !!(budget.budget >= minBudget && budget.start && budget.totalIntervals && budget.totalIntervals >= 1 && (moment(budget.start).isSameOrAfter(moment(campaign.startDate), 'day') || moment(budget.start).endOf('day').isAfter(getMinStartDate().startOf('day'))))
      }
      return false
    },
    [campaign, canBudgetBeEmpty]
  )

  const onBudgetChange = (budget: CampaignBudget) => {
    setCampaignBudget(budget)

    if (budget.method === BudgetMethod.Indefinite) {
      setScheduleSettings(prev => ({
        ...prev,
        indefinite: {
          budget: budget.budget,
          intervalUnit: budget.intervalUnit ?? IntervalUnit.Week
        }
      }))
    } else if (budget.method === BudgetMethod.Intervals) {
      setScheduleSettings(prev => ({
        ...prev,
        intervals: {
          budget: budget.budget,
          start: budget.start,
          totalIntervals: budget.totalIntervals ?? 1,
          intervalUnit: budget.intervalUnit ?? IntervalUnit.Week
        }
      }))
    } else if (budget.method === BudgetMethod.Dates) {
      setScheduleSettings(prev => ({
        ...prev,
        dates: {
          budget: budget.budget,
          start: budget.start,
          end: budget.end
        }
      }))
    }
  }

  const onBudgetMethodRadioChange = (optionId: string) => {
    let method: BudgetMethod
    if (optionId === 'indefinite') {
      method = BudgetMethod.Indefinite
      setCampaignBudget({
        budget: scheduleSettings.indefinite.budget,
        method,
        start: undefined,
        end: undefined,
        totalIntervals: undefined,
        intervalUnit: scheduleSettings.indefinite.intervalUnit
      })
    } else if (optionId === 'intervals') {
      method = BudgetMethod.Intervals
      setCampaignBudget({
        budget: scheduleSettings.intervals.budget,
        method,
        start: scheduleSettings.intervals.start || getMinStartDate(),
        end: undefined,
        totalIntervals: scheduleSettings.intervals.totalIntervals,
        intervalUnit: scheduleSettings.intervals.intervalUnit
      })
    } else {
      method = BudgetMethod.Dates
      setCampaignBudget({
        budget: scheduleSettings.dates.budget,
        method,
        start: scheduleSettings.dates.start || getMinStartDate(),
        end: scheduleSettings.dates.end,
        totalIntervals: undefined,
        intervalUnit: undefined
      })
    }
  }

  const budgetMethodRadios = [
    {
      id: 'indefinite',
      label: 'Run campaign indefinitely'
    },
    {
      id: 'intervals',
      label: 'Run campaign for a specific duration'
    },
    {
      id: 'dates',
      label: 'Run campaign within a specific date range'
    }
  ]

  if (!campaign) {
    return <EuiLoadingContent lines={10} />
  }

  return (
    <React.Fragment>
      <EuiSpacer />
      {campaign?.status === CampaignStatus.PaymentFailed && !campaign.isUnpaidSubscriptionPayment && (
        <React.Fragment>
          <UnpaidCallOut campaignId={campaign.id} hasBundle={campaign.hasBundle} />
          <EuiSpacer />
        </React.Fragment>
      )}
      <EuiFormRow
        label={
          <EuiText size='xs' color={'primary'}>
            <strong>Campaign Name</strong>
          </EuiText>
        }>
        <EuiFieldText
          name='name'
          value={name}
          onChange={e => {
            setName(e.target.value)
            markDirty('details')
          }}
          placeholder=''
          fullWidth
        />
      </EuiFormRow>
      <EuiSpacer size='xs' />

      <EuiSpacer size='m' />
      {canBudgetBeEmpty && (
        <>
          <EuiFormRow
            label={
              <EuiText size='xs' color={'primary'}>
                <strong>Budget Schedule</strong>
              </EuiText>
            }>
            <EuiRadioGroup options={budgetMethodRadios} idSelected={campaignBudget.method === BudgetMethod.Indefinite ? 'indefinite' : campaignBudget.method === BudgetMethod.Intervals ? 'intervals' : 'dates'} onChange={onBudgetMethodRadioChange} />
          </EuiFormRow>
          <EuiSpacer size='m' />
        </>
      )}
      <div style={canBudgetBeEmpty ? { marginLeft: '2rem' } : {}}>
        <AgencyV2CampaignBudgetSetter
          campaign={campaign}
          campaignBudget={campaignBudget}
          onBudgetChange={budget => {
            onBudgetChange(budget)
            markDirty('details')
          }}
        />
      </div>
      {(campaign.type === CampaignType.Internet || campaign.type === CampaignType.TV) && (
        <>
          <EuiFormRow>
            <EuiSwitch label={'Show Advanced Options'} checked={showAdvancedSettings} onChange={() => setShowAdvancedSettings(!showAdvancedSettings)} />
          </EuiFormRow>
          <EuiSpacer />
          {showAdvancedSettings && campaignDelivery && (
            <EditCampaignDeliveryStrategy
              campaignDelivery={campaignDelivery}
              onCampaignDeliveryChanged={cd => {
                setCampaignDelivery(cd)
                markDirty('details')
              }}
              campaignType={campaign.type}
            />
          )}
        </>
      )}
      <BudgetChangeModal campaign={campaign} isBudgetChangeModalVisible={isBudgetChangeModalVisible} name={name} budget={campaignBudget.budget} differenceAmount={amount} onConfirmComplete={onBudgetChangeModalComplete} onCancel={onBudgetChangeModalCancel} />
    </React.Fragment>
  )
}
